/**
 * StatusPipeline
 * Visual progress indicator for issue lifecycle.
 * Shows filled dots for completed states, highlighted current state,
 * and hollow dots for pending states.
 */

import { useTranslation } from 'react-i18next';

const PIPELINE_STEP_KEYS = ['reported', 'confirmed', 'repair_assigned', 'resolved', 'closed'] as const;

// Alternative terminal states that map into the pipeline
const STATUS_STEP_INDEX: Record<string, number> = {
  reported: 0,
  confirmed: 1,
  repair_assigned: 2,
  resolved: 3,
  cannot_repair: 3, // terminal at resolve level
  dismissed: 1,     // terminal at confirm level
  closed: 4,
};

interface StatusPipelineProps {
  status: string;
}

export default function StatusPipeline({ status }: StatusPipelineProps) {
  const { t } = useTranslation();
  const currentIndex = STATUS_STEP_INDEX[status] ?? 0;
  const isTerminal = status === 'dismissed';

  return (
    <div className="flex items-center gap-xs w-full">
      {PIPELINE_STEP_KEYS.map((stepKey, i) => {
        const isCurrent = i === currentIndex;
        const isCompleted = i < currentIndex;
        const isLast = i === PIPELINE_STEP_KEYS.length - 1;

        // For terminal states at this position, show the actual status label
        let label = t(`statusPipeline.${stepKey}`);
        if (isCurrent && status === 'dismissed') label = t('statusPipeline.dismissed');

        return (
          <div key={stepKey} className="flex items-center flex-1 min-w-0">
            {/* Dot + label */}
            <div className="flex flex-col items-center min-w-0">
              <div
                className={`w-3 h-3 rounded-full flex-shrink-0 transition-colors ${
                  isCurrent
                    ? isTerminal
                      ? 'bg-danger ring-2 ring-danger/30'
                      : 'bg-blue ring-2 ring-blue/30'
                    : isCompleted
                      ? 'bg-success'
                      : 'bg-secondary/30'
                }`}
              />
              <span
                className={`text-[10px] mt-xs truncate max-w-full ${
                  isCurrent
                    ? isTerminal
                      ? 'text-danger font-semibold'
                      : 'text-blue font-semibold'
                    : isCompleted
                      ? 'text-success'
                      : 'text-secondary'
                }`}
              >
                {label}
              </span>
            </div>

            {/* Connector line */}
            {!isLast && (
              <div
                className={`flex-1 h-0.5 mx-sm ${
                  isCompleted ? 'bg-success' : 'bg-secondary/20'
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
